import React, { useCallback } from "react";
import InputSection from "./InputsSection";
import ResultsSection from "./ResultsSection";
import ChartsSection from "./ChartsSection";
import "./Pvt.css";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import type { IField } from "../../interfaces/interfaces";
import { setInputsData } from "../../store/slices/pvtCalculatorSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";

const converter = new UnitConverter();

const PvtCalculator: React.FC = () => {
	const calculator = useAppSelector(state => state.pvtCalculator);
	const dispatch = useAppDispatch();
	const handlePaste = useCallback(
		(event: React.ClipboardEvent<HTMLElement>) => {
			if (!calculator) {
				return;
			}

			const clipboardText = event.clipboardData.getData("text");
			if (!clipboardText) {
				return;
			}

			const parsedValues = clipboardText
				.trim()
				.split(/\s+/)
				.map(token => Number.parseFloat(token.replace(",", ".")))
				.filter(value => Number.isFinite(value));

			if (parsedValues.length === 0) {
				return;
			}

			const target = event.target;
			if (!(target instanceof HTMLInputElement)) {
				return;
			}

			event.preventDefault();

			type InputBlock = 'inputs' | 'gInputs' | 'gasCompositionInputs';
			let targetBlock: InputBlock = 'inputs';
			let startIndex = 0;

			for (const block of ['inputs', 'gInputs', 'gasCompositionInputs'] as InputBlock[]) {
				const foundIndex = calculator[block].findIndex(
					input => input.id === target.id || input.queryName === target.name
				);
				if (foundIndex >= 0) {
					targetBlock = block;
					startIndex = foundIndex;
					break;
				}
			}

			const applyValues = (inputs: IField[]) =>
				inputs.map((input, index) => {
					const value = parsedValues[index - startIndex];
					if (value === undefined) {
						return input;
					}
					return {
						...input,
						currentValue: value,
						baseValue: input.unitCategoryId
							? converter.convert(value, input.currentUnitId, input.baseUnitId)
							: value,
						userValue: value,
						userUnitId: input.currentUnitId,
					};
				});


			dispatch(
				setInputsData({
					...calculator,
					[targetBlock]: applyValues(calculator[targetBlock]),
				})
			);
		},
		[dispatch, calculator]
	);

	return (
		<div className="pvt__container" onPasteCapture={handlePaste} tabIndex={0}>
			<InputSection />
			<ResultsSection />
			<ChartsSection />
		</div>
	)
}

export default PvtCalculator;
